// DownloadRevolution.jsx - FINAL WORKING VERSION WITH ACTUAL SIGNATURES

import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import Logo from "../../assets/images/logo1.png";

// Helper function to convert image URL to Base64 using proxy or direct fetch
const convertImageToBase64 = async (url) => {
  return new Promise(async (resolve, reject) => {
    try {
      console.log("Converting image to base64:", url);
      
      // Method 1: Try with fetch and credentials
      try {
        const response = await fetch(url, {
          mode: 'cors',
          credentials: 'include',
          headers: {
            'Accept': 'image/webp,image/png,image/jpeg,*/*'
          }
        });
        
        if (response.ok) {
          const blob = await response.blob();
          const reader = new FileReader();
          reader.onloadend = () => {
            console.log("✓ Image converted via fetch");
            resolve(reader.result);
          };
          reader.onerror = reject;
          reader.readAsDataURL(blob);
          return;
        }
      } catch (fetchError) {
        console.warn("Fetch failed, trying Image method:", fetchError.message);
      }
      
      // Method 2: Use Image with crossOrigin and canvas
      const img = new Image();
      img.crossOrigin = "Anonymous";
      
      img.onload = () => {
        try {
          const canvas = document.createElement('canvas');
          canvas.width = img.width;
          canvas.height = img.height;
          const ctx = canvas.getContext('2d');
          ctx.drawImage(img, 0, 0);
          const base64 = canvas.toDataURL('image/png');
          console.log("✓ Image converted via canvas");
          resolve(base64);
        } catch (canvasError) {
          console.error("Canvas conversion failed:", canvasError);
          reject(canvasError);
        }
      };
      
      img.onerror = (error) => {
        console.error("Image load failed:", error);
        reject(new Error(`Failed to load image: ${url}`));
      };
      
      // Add cache buster to avoid caching issues
      img.src = `${url}${url.includes('?') ? '&' : '?'}_cb=${Date.now()}`;
      
      // Timeout after 10 seconds
      setTimeout(() => {
        reject(new Error("Image load timeout"));
      }, 10000);
      
    } catch (error) {
      console.error("Error in convertImageToBase64:", error);
      reject(error);
    }
  });
};

export const downloadRevolutionPdf = async (data) => {
  if (!data) {
    console.error("No data provided for PDF generation");
    return;
  }

  console.log("Starting PDF generation for revolution:", data._id);
  
  // Get supporters array
  let supportersArray = data?.supporters || data?.supporter || [];
  console.log(`Found ${supportersArray.length} supporters`);
  
  // First, load all signature images and convert to Base64
  const supportersWithSignatures = [];
  
  for (let i = 0; i < supportersArray.length; i++) {
    const sup = supportersArray[i];
    const signatureUrl = sup?.digitalSignature || sup?.signature;
    
    console.log(`Processing supporter ${i + 1}: ${sup?.fullName}`);
    console.log(`Signature URL: ${signatureUrl}`);
    
    let signatureBase64 = null;
    
    if (signatureUrl) {
      try {
        signatureBase64 = await convertImageToBase64(signatureUrl);
        console.log(`✓ Signature loaded for ${sup?.fullName} (${signatureBase64.length} bytes)`);
      } catch (error) {
        console.error(`✗ Failed to load signature for ${sup?.fullName}:`, error.message);
      }
    } else {
      console.log(`No signature URL for ${sup?.fullName}`);
    }
    
    supportersWithSignatures.push({
      name: sup?.fullName || "Anonymous",
      mobile: sup?.phone || "N/A",
      signatureBase64: signatureBase64,
      place: sup?.districtPrivate && sup?.statePrivate 
        ? `${sup.districtPrivate}, ${sup.statePrivate}`
        : sup?.districtPrivate || sup?.statePrivate || "N/A",
      comment: sup?.comment || "(No comment)",
      date: sup?.createdAt ? new Date(sup.createdAt).toLocaleDateString() : new Date().toLocaleDateString(),
    });
  }

  // Create PDF
  const pdf = new jsPDF("p", "mm", "a4");

  // ================= ADD LOGO =================
  try {
    const logo = new Image();
    logo.src = Logo;
    await new Promise((resolve) => {
      logo.onload = resolve;
      setTimeout(resolve, 1000);
    });
    pdf.addImage(logo, "PNG", 80, 8, 50, 18);
  } catch (err) {
    console.warn("Logo error:", err);
  }

  // ================= HEADER SECTION =================
  pdf.setFont("helvetica", "bold");
  pdf.setFontSize(16);
  pdf.text("Online Revolution – Bharat Jagriti", 105, 32, { align: "center" });

  pdf.setFont("helvetica", "normal");
  pdf.setFontSize(10);

  // Each line is 6mm apart, label and value on same Y position
  pdf.text(`Revolution ID : ${data?._id || "-"}`, 14, 42);
  pdf.text(`Organised By  : ${data?.from?.fullName || "-"}`, 14, 48);
  pdf.text(`Date          : ${data?.createdAt ? new Date(data.createdAt).toLocaleDateString() : "-"}`, 14, 54);
  pdf.text(`Visibility    : ${data?.visibility || "-"}`, 14, 60);

  // Title — label bold, value normal, same line
  pdf.setFont("helvetica", "bold");
  pdf.setFontSize(10);
  pdf.text("Title :", 14, 66);
  pdf.setFont("helvetica", "normal");
  pdf.text(data?.title || "-", 30, 66, { maxWidth: 165 });

  // To — label bold, value normal, same line
  pdf.setFont("helvetica", "bold");
  pdf.text("To :", 14, 72);
  pdf.setFont("helvetica", "normal");
  pdf.text(data?.to || "-", 30, 72, { maxWidth: 165 });

  // Subject — label bold, value normal, same line
  pdf.setFont("helvetica", "bold");
  pdf.text("Subject :", 14, 78);
  pdf.setFont("helvetica", "normal");
  pdf.text(data?.subject || data?.title || "-", 36, 78, { maxWidth: 158 });

  // Description — label bold on its own line, value below
  pdf.setFont("helvetica", "bold");
  pdf.text("Description :", 14, 86);
  pdf.setFont("helvetica", "normal");
  const descLines = pdf.splitTextToSize(data?.description || "-", 170);
  pdf.text(descLines, 14, 92, { lineHeightFactor: 1.5 });

  let currentY = 92 + (descLines.length * 6) + 10;

  // ================= CREATE TABLE DATA =================
  const tableBody = supportersWithSignatures.length > 0
    ? supportersWithSignatures.map(s => [s.name, s.mobile, "", s.place, s.comment, s.date])
    : [["No supporters yet", "", "", "", "", ""]];

  // Store signature positions
  const signaturePositions = [];

  // ================= RENDER TABLE =================
  autoTable(pdf, {
    startY: currentY,
    head: [["Name", "Mobile", "Signature", "Place", "Comment", "Date"]],
    body: tableBody,
    styles: {
      fontSize: 9,
      halign: "center",
      valign: "middle",
      cellPadding: 3,
      lineWidth: 0.1,
    },
    headStyles: {
      fillColor: [52, 73, 94],
      textColor: [255, 255, 255],
      fontStyle: "bold",
    },
    columnStyles: {
      0: { cellWidth: 30, halign: "left" },
      1: { cellWidth: 25 },
      2: { cellWidth: 30, minCellHeight: 15 },
      3: { cellWidth: 25, halign: "left" },
      4: { cellWidth: 45, halign: "left" },
      5: { cellWidth: 25 },
    },
    didDrawCell: (dataCell) => {
      // Store signature cell positions
      if (dataCell.section === "body" && dataCell.column.index === 2) {
        const rowIndex = dataCell.row.index;
        if (rowIndex < supportersWithSignatures.length) {
          signaturePositions[rowIndex] = {
            x: dataCell.cell.x,
            y: dataCell.cell.y,
            width: dataCell.cell.width,
            height: dataCell.cell.height
          };
          console.log(`Stored position for row ${rowIndex}: x=${dataCell.cell.x}, y=${dataCell.cell.y}`);
        }
      }
    },
    margin: { left: 14, right: 14 },
  });

  // ================= DRAW SIGNATURES =================
  console.log("Drawing signatures on PDF...");
  
  for (let i = 0; i < supportersWithSignatures.length; i++) {
    const supporter = supportersWithSignatures[i];
    const position = signaturePositions[i];
    
    if (supporter.signatureBase64 && position) {
      try {
        const imgWidth = 25;
        const imgHeight = 10;
        const x = position.x + (position.width / 2) - (imgWidth / 2);
        const y = position.y + (position.height / 2) - (imgHeight / 2);
        
        pdf.addImage(supporter.signatureBase64, 'PNG', x, y, imgWidth, imgHeight);
        console.log(`✓ Signature drawn for ${supporter.name} at (${x}, ${y})`);
      } catch (error) {
        console.error(`✗ Failed to draw signature for ${supporter.name}:`, error);
      }
    } else if (!supporter.signatureBase64) {
      console.log(`No signature base64 for ${supporter.name}`);
      // Draw placeholder text
      if (position) {
        pdf.setFontSize(8);
        pdf.setTextColor(150, 150, 150);
        pdf.text("No signature", position.x + (position.width / 2), position.y + (position.height / 2), { align: "center" });
      }
    }
  }

  // Save PDF
  const fileName = `revolution_${data?._id || Date.now()}.pdf`;
  pdf.save(fileName);
  console.log("PDF generation complete with", supportersWithSignatures.filter(s => s.signatureBase64).length, "signatures");
};


// import jsPDF from "jspdf";
// import autoTable from "jspdf-autotable";
// import Logo from "../../assets/images/logo1.png";

// export const downloadRevolutionPdf = async (data) => {
//   const pdf = new jsPDF("p", "mm", "a4");

//   /* ================= LOGO ================= */
//   const logo = new Image();
//   logo.src = Logo;

//   await new Promise((resolve) => {
//     logo.onload = resolve;
//     logo.onerror = resolve;
//   });

//   pdf.addImage(logo, "PNG", 80, 8, 50, 18);

//   /* ================= TITLE ================= */
//   pdf.setFont("helvetica", "bold");
//   pdf.setFontSize(16);
//   pdf.text("Online Revolution – Bharat Jagriti", 105, 32, { align: "center" });

//   /* ================= META ================= */
//   pdf.setFont("helvetica", "normal");
//   pdf.setFontSize(10);

//   pdf.text(`Revolution ID : ${data?._id || "-"}`, 14, 42);
//   pdf.text(`Organised By : ${data?.from?.fullName || "-"}`, 14, 48);

//   pdf.text(
//     `Date : ${
//       data?.createdAt
//         ? new Date(data.createdAt).toLocaleDateString()
//         : "-"
//     }`,
//     14,
//     54
//   );

//   /* ================= TO ================= */
//   pdf.setFontSize(11);
//   pdf.text("To :", 14, 64);

//   pdf.setFontSize(10);
//   pdf.text(data?.to || "-", 25, 64, { maxWidth: 170 });

//   /* ================= SUBJECT ================= */
//   pdf.setFontSize(11);
//   pdf.text("Subject :", 14, 72);

//   pdf.setFontSize(10);
//   pdf.text(data?.title || "-", 35, 72, { maxWidth: 160 });

//   /* ================= DESCRIPTION ================= */
//   pdf.setFontSize(11);
//   pdf.setFont("helvetica", "bold");
//   pdf.text("Description :", 14, 80);

//   pdf.setFont("helvetica", "normal");
//   pdf.setFontSize(10);

//   const descX = 40;
//   const descY = 80;

//   const descLines = pdf.splitTextToSize(
//     data?.description || "-",
//     180 - (descX - 14)
//   );

//   pdf.text(descLines, descX, descY, {
//     lineHeightFactor: 1.5,
//   });

//   let currentY = descY + descLines.length * 6 + 6;

//   /* ================= IMAGES ================= */
//   if (data?.images?.length) {
//     pdf.setFontSize(11);
//     pdf.text("Images :", 14, currentY);
//     currentY += 6;

//     const imgW = 60;
//     const imgH = 40;
//     let x = 14;

//     for (let imgPath of data.images) {
//       if (!imgPath) continue;

//       // new row
//       if (x + imgW > 190) {
//         x = 14;
//         currentY += imgH + 6;
//       }

//       // new page
//       if (currentY + imgH > 280) {
//         pdf.addPage();
//         currentY = 20;
//         x = 14;
//       }

//       const img = new Image();
//       img.crossOrigin = "*";
//       img.src = imgPath;

//       await new Promise((resolve) => {
//         img.onload = resolve;
//         img.onerror = resolve;
//       });

//       try {
//         pdf.addImage(img, "JPEG", x, currentY, imgW, imgH);
//       } catch (e) {
//         console.log("Image skip:", imgPath);
//       }

//       x += imgW + 6;
//     }

//     currentY += imgH + 10;
//   }

//   /* ================= SUPPORTERS DATA ================= */
//   const supporters = (data?.supporter || []).map((sup) => ({
//     name: sup?.user?.fullName || "-",
//     mobile: sup?.user?.phone || "-",
//     signature: sup?.digitalSignature || null,
//     place: sup?.user?.statePrivate || "-",
//     comment: sup?.comment || "-",
//     date: sup?.createdAt
//       ? new Date(sup.createdAt).toLocaleDateString()
//       : "-",
//   }));

//   /* ================= LOAD SIGNATURE IMAGES (CRASH FIX) ================= */
//   for (let s of supporters) {
//     if (s.signature) {
//       const img = new Image();
//       img.crossOrigin = "anonymous";
//       img.src = s.signature;

//       await new Promise((resolve) => {
//         img.onload = () => {
//           s.signatureImg = img;
//           resolve();
//         };
//         img.onerror = () => resolve();
//       });
//     }
//   }

//   /* ================= TABLE ================= */
//   autoTable(pdf, {
//     startY: currentY,

//     head: [[
//       "Name",
//       "Mobile",
//       "Signature",
//       "Place",
//       "Comment",
//       "Date",
//     ]],

//     body: supporters.map((s) => [
//       s.name,
//       s.mobile,
//       "",
//       s.place,
//       s.comment,
//       s.date,
//     ]),

//     styles: {
//       fontSize: 9,
//       halign: "center",
//       valign: "middle",
//       cellPadding: 3,
//       lineWidth: 0.3,
//       lineColor: [0, 0, 0],
//     },

//     headStyles: {
//       fillColor: [245, 245, 245],
//       textColor: 0,
//       fontStyle: "bold",
//       lineWidth: 0.4,
//     },

//     columnStyles: {
//       0: { cellWidth: 32 },
//       1: { cellWidth: 26 },
//       2: { cellWidth: 28, minCellHeight: 14 },
//       3: { cellWidth: 26 },
//       4: { cellWidth: 42 },
//       5: { cellWidth: 26 },
//     },

//     didDrawCell: (dataCell) => {
//       if (
//         dataCell.section === "body" &&
//         dataCell.column.index === 2
//       ) {
//         const rowIndex = dataCell.row.index;
//         const signImg = supporters[rowIndex]?.signatureImg;

//         if (signImg) {
//           const imgW = 20;
//           const imgH = 8;

//           const x =
//             dataCell.cell.x +
//             dataCell.cell.width / 2 -
//             imgW / 2;

//           const y =
//             dataCell.cell.y +
//             dataCell.cell.height / 2 -
//             imgH / 2;

//           try {
//             pdf.addImage(signImg, "PNG", x, y, imgW, imgH);
//           } catch (e) {
//             console.log("Signature skip");
//           }
//         }
//       }
//     },

//     pageBreak: "auto",
//     rowPageBreak: "avoid",
//     showHead: "everyPage",
//   });

//   /* ================= SAVE ================= */
//   pdf.save(`revolution-${data?._id}.pdf`);
// };  